import { defineStore } from 'pinia';
import { useWebportalStore } from '@/stores/webportal';
import { useAuthStore } from '@/stores/auth';

//Import models
import { MenuItem } from '@/models/MenuItem'
import { useScanStore } from './scan';
import VLApi from '@/api/VLApi';
import { $ResetPinia } from './ExtendedPinia';
import router from '@/router';
import { Browser } from '@capacitor/browser';
import { UseIonRouterResult } from '@ionic/vue';

export function parseMenuItem(menuItem: any): MenuItem {
  let subItems : MenuItem[] | undefined;
  if (menuItem.children && menuItem.children.length > 0) {
    subItems = [];
    for (let i = 0; i < menuItem.children.length; i++) {
      subItems.push(parseMenuItem(menuItem.children[i]));
    }
  }

  if(menuItem.type == 1) {
    // Virtwass Web menu item
    return {
      ID: menuItem.id,
      SubItems: subItems,
      Tekst: menuItem.tekst,
      Url: (menuItem.component ? `${useWebportalStore().virtwassUrl}login?webportalToken=${useAuthStore().VLtoken}&group=${menuItem.hoofdgroep}&path=${menuItem.name}` : ''),
      FromVirtWass: true,
      Embed: false,
      MenuIcon: menuItem.menuIcon,
      WeergevenOpHomeIcons: false,
      WeergevenInMenu: true
    }
  }
  else {
    // Webportal menu item
    return {
      ID: menuItem.id,
      SubItems: subItems,
      Tekst: menuItem.tekst,
      Url: menuItem.url,
      FromVirtWass: false,
      Embed: menuItem.embed,
      Icon: menuItem.icon,
      WeergevenOpHomeIcons: menuItem.weergevenOpHomeIcons,
      WeergevenInMenu: menuItem.weergevenInMenu,
      MenuIcon: menuItem.menuIcon,
      Content: menuItem.content,
      RapportNo: menuItem.rapportNo,
      ExcelRapportNo: menuItem.excelRapportNo
    }
  }
}

export const useMenuStore = defineStore('menu', {
  state: () => ({
    menuItems: [] as MenuItem[],
    currentItem: null as MenuItem | null
  }),
  actions: {
    //Get menu items for user
    async getMenuItems() {
      //Check if user is authenticated, else stop
      if (!useAuthStore().VLtoken || !useWebportalStore().selectedWebportal?.ServiceUrl) {
        return "login not valid"
      }
      //Getting menu items for authenticated user
      try {
        const response = await VLApi.get(`${useWebportalStore()?.vlApiUrl ?? ''}/menu/getMenuItems`);
        
        const data = [] as MenuItem[];
        if (response.data && response.data.data.length > 0) {
          for (let i = 0; i < response.data.data.length; i++) {
            data.push(parseMenuItem(response.data.data[i]));
          }
        }

        //Store menuItems in state
        this.menuItems = data.map((item) => {

          // Foreach items in first layer of subItems remove #/
          if (item.SubItems) {
            item.SubItems.forEach((subItem) => {
              if (subItem.Url && subItem.Url.startsWith('#/')) {
                subItem.Url = subItem.Url.slice(1);
              }
            });
          }

          // Foreach main item (no sub item) remove #/ and trim "/true" off the Url
          if (item.Url && (item.Url.startsWith('#/') || item.Url.startsWith(window.location.origin + window.location.pathname + '#/'))) {
            if (item.Url.startsWith(window.location.origin + window.location.pathname + '#/')) {
              item.Url = item.Url.substring(window.location.origin.length + window.location.pathname.length);
            }
            const url = item.Url.slice(1).replace(/\/true$/, '');
            if (url.startsWith('/scan')) {
              const match = url.match(/\/scan\/([\d]+)\/([\d]+)(?:\?positie=([\d]+)&scanstation=([\d]+))?/);
              if (match) {
                const positie = match[3] ?? match[1]
                const station = match[4] ?? match[2]
                const scanStore = useScanStore(positie, station)
                scanStore.GetScanPosition(+positie).then(() => {
                  scanStore.GetScanStation(+station).then(() => {
                    scanStore.GetScanOverview(+positie, +station);
                  })
                });
              }
            }
            return { ...item, Url: url };
          } else {
            return item;
          }
        });
      } catch (error: any) {
        console.error(error);
        useAuthStore().logout();
        $ResetPinia();
        router.push('/login');
      }
    },
    //Handle's the redirect of menu items
    async handleMenuNavigation(item: MenuItem, router: UseIonRouterResult, push?: boolean) {
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
      const url = item.Url;
      this.currentItem = item;
      if (item.Embed) {
        if (push) {
          router.push(`/embed/${item.ID}`)
        } else {
          router.navigate(`/embed/${item.ID}`, "root");
        }
        return true;
      } else if (item.Content || item.Content == "") {
        if (push) {
          router.push(`/content/${item.ID}`)
        } else {
          router.navigate(`/content/${item.ID}`, "root");
        }
        
        return true;
      }
      else if (url?.startsWith("http")) {
        if (isMobile) {
          await Browser.open({ url });
        } else {
          if (item.FromVirtWass) {
            window.open(url, "_self", "noopener noreferrer");
          }
          else {
            window.open(url, "_blank", "noopener noreferrer");
          }
        }
      } else {
        if (push) {
          router.push(url)
        } else {
          router.navigate(url, "root");
        }
        return true;
      }
    }
  },
  getters: {
    homePage: (state) => {
      return state.menuItems.find(x => x.Url != '/logout' && x.FromVirtWass == false)
    }
  }
})